// Dashboard Page Component - Admin health overview with tabbed data sync

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, StatusBadge, Tabs, TabList, Tab, TabPanel } from '../components/common';
import { formatDate } from '../utils/dateFormatters';
import { adminService } from '../services/admin';
import StatisticsTab from '../components/statistics/StatisticsTab';
import MySQLSyncTab from '../components/dashboard/MySQLSyncTab';
import DataSourcesTab from '../components/statistics/DataSourcesTab';
import type { SyncStatus, SystemHealth, RagStats, DocumentSyncHealth } from '../types/api';
import { RefreshIcon, DatabaseIcon, ServerIcon, SyncIcon, WorkerIcon } from '../components/icons';
import styles from './DashboardPage.module.css';

// Health status badge
function HealthBadge({ status }: { status: string }) {
  const variant = status === 'healthy' ? 'success'
    : status === 'running' ? 'info'
    : status === 'degraded' ? 'warning'
    : 'error';
  return (
    <StatusBadge variant={variant} pulse={status === 'running'}>
      {status}
    </StatusBadge>
  );
}

// Component health card
interface HealthCardProps {
  title: string;
  icon: React.ReactNode;
  status: string;
  details?: string;
}

function HealthCard({ title, icon, status, details }: HealthCardProps) {
  return (
    <div className={styles.healthCard}>
      <div className={styles.healthIcon}>{icon}</div>
      <div className={styles.healthInfo}>
        <span className={styles.healthTitle}>{title}</span>
        <HealthBadge status={status} />
        {details && <span className={styles.healthDetails}>{details}</span>}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [ragStats, setRagStats] = useState<RagStats | null>(null);
  const [docSyncHealth, setDocSyncHealth] = useState<DocumentSyncHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);

  const fetchData = useCallback(async (opts?: { fresh?: boolean }) => {
    try {
      setError(null);
      const [syncData, healthData, ragData, docHealth] = await Promise.all([
        adminService.getSyncStats({ fresh: opts?.fresh }),
        adminService.getSystemHealth(),
        adminService.getRagStats().catch(() => null),
        adminService.getDocumentSyncHealth().catch(() => null),
      ]);
      setSyncStatus(syncData);
      setSystemHealth(healthData);
      setRagStats(ragData);
      setDocSyncHealth(docHealth);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleTriggerSync = async (
    syncType: 'full' | 'incremental' | 'customers' | 'products' | 'salespersons' | 'sales_history' | 'product_costs' | 'ftp'
  ) => {
    setIsTriggering(true);
    try {
      // FTP sync not yet implemented on backend - only trigger for MySQL syncs
      if (syncType !== 'ftp') {
        await adminService.triggerSync(syncType);
      }
      // Refresh status after triggering — bypass the server memo so the
      // job just queued is in recent_jobs now, not at the next window.
      await fetchData({ fresh: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger sync');
    } finally {
      setIsTriggering(false);
    }
  };


  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1>Admin Dashboard</h1>
          {systemHealth && (
            <HealthBadge status={systemHealth.status} />
          )}
        </div>
        <div className={styles.actions}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData({ fresh: true })}
            leftIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className={styles.errorCard}>
          <CardBody>
            <p className={styles.errorText}>{error}</p>
          </CardBody>
        </Card>
      )}

      {/* System Health Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>System Health</h2>
        <div className={styles.healthGrid}>
          <HealthCard
            title="Database"
            icon={<DatabaseIcon />}
            status={systemHealth?.components.database.status ?? 'unknown'}
            details={systemHealth?.components.database.latency_ms
              ? `${systemHealth.components.database.latency_ms}ms latency`
              : undefined}
          />
          <HealthCard
            title="Redis"
            icon={<ServerIcon />}
            status={systemHealth?.components.redis.status ?? 'unknown'}
            details={systemHealth?.components.redis.latency_ms
              ? `${systemHealth.components.redis.latency_ms}ms latency`
              : undefined}
          />
          <HealthCard
            title="Celery Workers"
            icon={<WorkerIcon />}
            status={systemHealth?.components.celery.status ?? 'unknown'}
            details={systemHealth?.components.celery.workers !== undefined
              ? `${systemHealth.components.celery.workers} worker(s)`
              : undefined}
          />
          <HealthCard
            title="Data Sync"
            icon={<SyncIcon />}
            status={systemHealth?.components.sage_sync.status ?? 'unknown'}
            details={systemHealth?.components.sage_sync.last_run
              ? `Last: ${formatDate(systemHealth.components.sage_sync.last_run)}`
              : undefined}
          />
        </div>
      </section>

      {/* Data Sync Section with Tabs */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Data Sync</h2>

        <Tabs defaultTab="mysql">
          <TabList>
            <Tab value="statistics">Statistics</Tab>
            <Tab value="data-sources">Data Sources</Tab>
            <Tab value="mysql">MySQL Sync</Tab>
            <Tab value="ftp">FTP Documents</Tab>
          </TabList>

          <TabPanel value="statistics">
            <StatisticsTab />
          </TabPanel>

          <TabPanel value="data-sources">
            <DataSourcesTab />
          </TabPanel>

          {/* Unified MySQL Sync Tab */}
          <TabPanel value="mysql">
            <MySQLSyncTab
              syncStatus={syncStatus}
              isTriggering={isTriggering}
              onTriggerSync={(syncType) => handleTriggerSync((syncType || 'full') as 'full' | 'incremental')}
            />
          </TabPanel>

          {/* FTP Sync Tab */}
          <TabPanel value="ftp">
            <div className={styles.tabHeader}>
              <p className={styles.tabDescription}>
                Technical documents (SDS, TDS, LEED) synced from FTP and processed through the RAG pipeline.
              </p>
              <div className={styles.headerButtons}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/rag')}
                >
                  RAG Admin
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleTriggerSync('ftp')}
                  isLoading={isTriggering}
                >
                  Trigger FTP Sync
                </Button>
              </div>
            </div>

            {/* Document Pipeline Stats */}
            <div className={styles.statsGrid}>
              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Total Documents</span>
                    <span className={styles.statValue}>
                      {ragStats?.documents?.total?.toLocaleString() ?? docSyncHealth?.documents?.total?.toLocaleString() ?? 0}
                    </span>
                    <span className={styles.statMeta}>In RAG pipeline</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Indexed</span>
                    <span className={styles.statValue} style={{ color: 'var(--color-success)' }}>
                      {ragStats?.documents?.by_status?.indexed?.toLocaleString() ?? docSyncHealth?.documents?.by_status?.indexed?.toLocaleString() ?? 0}
                    </span>
                    <span className={styles.statMeta}>Ready for search</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Processing</span>
                    <span className={styles.statValue} style={{ color: 'var(--color-info)' }}>
                      {(
                        (ragStats?.documents?.by_status?.pending ?? 0) +
                        (ragStats?.documents?.by_status?.downloaded ?? 0) +
                        (ragStats?.documents?.by_status?.extracted ?? 0) +
                        (ragStats?.documents?.by_status?.chunked ?? 0) +
                        (ragStats?.documents?.by_status?.summarized ?? 0)
                      ).toLocaleString()}
                    </span>
                    <span className={styles.statMeta}>In pipeline</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Failed</span>
                    <span className={styles.statValue} style={{ color: 'var(--color-error)' }}>
                      {ragStats?.documents?.by_status?.failed?.toLocaleString() ?? docSyncHealth?.documents?.by_status?.failed?.toLocaleString() ?? 0}
                    </span>
                    <span className={styles.statMeta}>Need attention</span>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Chunk Stats */}
            <div className={styles.statsGrid}>
              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Total Chunks</span>
                    <span className={styles.statValue}>{ragStats?.chunks?.total?.toLocaleString() ?? 0}</span>
                    <span className={styles.statMeta}>Document segments</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Useful Chunks</span>
                    <span className={styles.statValue}>{ragStats?.chunks?.useful?.toLocaleString() ?? 0}</span>
                    <span className={styles.statMeta}>Searchable content</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>With Summaries</span>
                    <span className={styles.statValue}>{ragStats?.chunks?.with_summary?.toLocaleString() ?? 0}</span>
                    <span className={styles.statMeta}>AI-summarized</span>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Total Tokens</span>
                    <span className={styles.statValue}>{ragStats?.chunks?.total_tokens?.toLocaleString() ?? 0}</span>
                    <span className={styles.statMeta}>Indexed content</span>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Document Types */}
            {ragStats?.documents?.by_type && Object.keys(ragStats.documents.by_type).length > 0 && (
              <Card className={styles.infoCard}>
                <CardHeader>
                  <h3>Documents by Type</h3>
                </CardHeader>
                <CardBody>
                  <div className={styles.typeGrid}>
                    {Object.entries(ragStats.documents.by_type).map(([type, count]) => (
                      <div key={type} className={styles.typeItem}>
                        <span className={styles.typeLabel}>{type.toUpperCase()}</span>
                        <span className={styles.typeCount}>{count}</span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Pipeline Status Card */}
            <Card className={styles.infoCard}>
              <CardHeader>
                <h3>Pipeline Status</h3>
                {docSyncHealth && <HealthBadge status={docSyncHealth.status} />}
              </CardHeader>
              <CardBody>
                <p className={styles.infoText}>
                  Documents are automatically synced from the FTP server, processed through Document AI
                  for text extraction, chunked with semantic boundaries, summarized by AI, and uploaded
                  to Vertex AI Search for retrieval. Use the RAG Admin page to monitor individual
                  document progress and retry failed documents.
                </p>
                {docSyncHealth?.last_sync?.started_at && (
                  <p className={styles.infoMeta}>
                    Last sync: {formatDate(docSyncHealth.last_sync.started_at)}
                    {docSyncHealth.last_sync?.status && ` (${docSyncHealth.last_sync.status})`}
                  </p>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </Tabs>
      </section>
    </div>
  );
}
